import { useEffect, useMemo, useState } from "react";
import type { Account, AccountId, AccountKind, Pending } from "../types";
import { fmtPct, fmtPx, fmtUSD } from "../lib/format";
import {
  destinationLabel,
  divergenceText,
  resolveAcks,
  resolveMode,
  type AckKey,
} from "../lib/approval";

// Approval modal — UX §8 + PRD §5.9.2 + §5.9.4. Implements all three
// modes (Express, Standard, Careful), live-additive ack, and PF-gap
// intervention. Approve button is disabled until every required ack is
// checked. ⌘↵ approves only when ready; Esc cancels.

// The acknowledgments the operator actually ticked, surfaced to the
// caller so the real-money ack reaches the server (UX §8.7 / UX.md:1059).
export type ApprovalAcks = {
  user_acknowledged_live_real_money: boolean;
};

type Props = {
  pending: Pending;
  onClose: () => void;
  onApprove: (acks: ApprovalAcks) => void | Promise<void>;
  onResize?: (riskDollars: string) => Promise<void>;
  onOpenRouteSettings?: () => void;
  isPlacing?: boolean;
  issue?: ApprovalModalIssue | null;
};

export type ApprovalModalIssue = {
  kind:
    | "approval_blocked"
    | "limits_changed"
    | "price_drift"
    | "order_placement_failed";
  detail: string;
  blocking?: boolean;
};

const emptyAckState = (): Record<AckKey, boolean> => ({
  standard_risk: false,
  careful_risk: false,
  careful_gap: false,
  careful_diverge: false,
  live_real_money: false,
  pf_gap_intervention: false,
});

const formatCountdown = (s: number): string => {
  const safe = Math.max(0, s);
  const mm = String(Math.floor(safe / 60));
  const ss = String(safe % 60).padStart(2, "0");
  return `${mm}:${ss}`;
};

const limitStatusLabel = (status: Pending["limitChecks"][number]["status"]) => {
  if (status === "pass") return "Pass";
  if (status === "warn") return "Warn";
  if (status === "fail") return "Fail";
  return "N/A";
};

// Derive a minimal Account shape from the Pending's account id + label.
// The fixture lookup (getAccount) has been removed so the fixture module
// is not pulled into production bundles. The Pending wire type already
// carries everything we need: id → kind mapping, label from accountLabel.
const _KIND_BY_ID: Record<AccountId, AccountKind> = {
  paper: "trading-paper",
  live: "trading-live",
  invest: "investment",
};

function pendingToAccount(pending: Pending): Account {
  const kind = _KIND_BY_ID[pending.account] ?? "trading-paper";
  return {
    id: pending.account,
    brokerAccountId: pending.brokerAccountId,
    kind,
    label: pending.accountLabel,
    broker: "",
    mask: "",
    equity: 0,
    todayPL: 0,
    todayPLPct: 0,
    sinceStartPL: 0,
    strategiesBound: 0,
    reconciledSec: 0,
    status: "connected",
  };
}

export const ApprovalModal = ({
  pending: t,
  onClose,
  onApprove,
  onResize,
  onOpenRouteSettings,
  isPlacing = false,
  issue = null,
}: Props) => {
  const account = pendingToAccount(t);
  const mode = useMemo(() => resolveMode(t), [t]);
  const acks = useMemo(() => resolveAcks(t, mode), [t, mode]);
  const div = divergenceText(t.ai, t.det);
  const preview = t.currentPricePreview ?? t.preview ?? null;
  const targetGain = preview?.targetGainDollars ?? null;
  const rr = preview?.riskRewardRatio ?? null;

  const [checked, setChecked] = useState<Record<AckKey, boolean>>(
    emptyAckState,
  );
  const [riskInput, setRiskInput] = useState(() =>
    String((preview?.actualRiskDollars ?? t.risk).toFixed(2)),
  );
  const [resizeError, setResizeError] = useState<string | null>(null);
  const [resizing, setResizing] = useState(false);

  const ready = acks.every((a) => checked[a.key]);
  // The acks the operator actually ticked. The live real-money ack is
  // only a required (and thus rendered) checkbox for live accounts; for
  // paper it stays false and the server ignores it. Approve is gated on
  // `ready`, so when this fires for a live row the checkbox IS ticked.
  const approveAcks = (): ApprovalAcks => ({
    user_acknowledged_live_real_money: checked.live_real_money,
  });
  const blocked = issue?.blocking === true;
  const parsedRiskInput = Number(riskInput);
  const riskInputValid = Number.isFinite(parsedRiskInput) && parsedRiskInput > 0;
  const sizeDirty =
    riskInput.trim() !== "" &&
    Math.abs(parsedRiskInput - (preview?.actualRiskDollars ?? t.risk)) > 0.005;

  const [countdown, setCountdown] = useState(Math.max(0, t.expiresInSec));
  useEffect(() => {
    setChecked(emptyAckState());
    setCountdown(Math.max(0, t.expiresInSec));
    setRiskInput(String((preview?.actualRiskDollars ?? t.risk).toFixed(2)));
    setResizeError(null);
  }, [t.approvalId, t.expiresInSec, t.risk, preview?.actualRiskDollars]);
  useEffect(() => {
    if (countdown <= 0) {
      return;
    }
    const id = window.setTimeout(() => setCountdown(countdown - 1), 1000);
    return () => window.clearTimeout(id);
  }, [countdown]);

  // ⌘↵ approves only when ready (UX §8.5: "approves only after all
  // required checkboxes are checked"). Esc cancels.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        if (isPlacing) {
          return;
        }
        onClose();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        if (
          ready &&
          countdown > 0 &&
          !isPlacing &&
          !blocked &&
          riskInputValid &&
          !sizeDirty &&
          !resizing
        ) {
          void onApprove(approveAcks());
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    ready,
    countdown,
    onApprove,
    onClose,
    isPlacing,
    blocked,
    riskInputValid,
    sizeDirty,
    resizing,
  ]);

  const expired = countdown <= 0;
  const countdownColor = expired
    ? "var(--tp-bad)"
    : countdown <= 10
      ? "var(--tp-warn)"
      : "var(--tp-fg-3)";

  // Risk display label per PRD §5.9.2 + UX §8.6.7. Live carries REAL,
  // paper carries the "paper" prefix, investment uses allocation %.
  const riskValueLabel =
    t.account === "live"
      ? `${fmtUSD(t.risk)} REAL`
      : t.account === "paper"
        ? `paper ${fmtUSD(t.risk)}`
        : fmtUSD(t.risk);

  const saveSize = async () => {
    if (!onResize || !riskInputValid || !sizeDirty) {
      return;
    }
    setResizeError(null);
    setResizing(true);
    try {
      await onResize(parsedRiskInput.toFixed(2));
    } catch (e) {
      setResizeError(e instanceof Error ? e.message : "Size update failed");
    } finally {
      setResizing(false);
    }
  };

  return (
    <div
      className="tp-modal-back"
      onClick={isPlacing ? undefined : onClose}
      role="presentation"
    >
      <div
        className={"tp-modal " + (mode === "express" ? "express" : "")}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="approval-modal-title"
      >
        {/* Header */}
        <div className="tp-modal-head">
          <div>
            <div className={"tp-mode-strip " + mode}>
              {mode === "express" && "⚡ Express approval"}
              {mode === "standard" && "Standard approval"}
              {mode === "careful" && "⚠ Careful review"}
            </div>
            <div className="title" id="approval-modal-title">
              <span className="ticker">{t.sym}</span> · {t.side} ·{" "}
              <span
                style={{
                  color: "var(--tp-fg-3)",
                  fontFamily: "var(--tp-font-mono)",
                }}
              >
                {t.strategy}
              </span>
              {t.companyName ? (
                <div className="tp-symbol-company">{t.companyName}</div>
              ) : null}
            </div>
            {/* Destination indicator (UX §8.6.7) */}
            <div className={"tp-destination " + account.kind}>
              Destination: {destinationLabel(account, mode)}
              {onOpenRouteSettings && (
                <button
                  type="button"
                  className="tp-btn tp-btn-secondary tp-btn-sm"
                  onClick={onOpenRouteSettings}
                  disabled={isPlacing}
                  style={{ marginLeft: 8 }}
                >
                  Route settings
                </button>
              )}
            </div>
            {mode === "careful" && t.careful && (
              <div className="tp-careful-reasons">
                Reasons:
                {[
                  t.careful.liveAccount && "live account",
                  t.careful.aiDivergence &&
                    `AI/Det diverge by ${Math.abs(t.ai - t.det)}pts`,
                  t.careful.pfGapHigh &&
                    `paper-vs-live PF gap ${t.pfGapPct}%`,
                  t.careful.basketNearLimit &&
                    `basket near cap (${t.basket?.warningPctOfCap}%)`,
                  t.careful.firstSymbolFromStrategy &&
                    "first trade in this symbol from this strategy",
                ]
                  .filter(Boolean)
                  .map((r, i, arr) => (
                    <span key={i}>
                      {" "}
                      {String.fromCharCode(0x2460 + i)} {r}
                      {i < arr.length - 1 ? " " : ""}
                    </span>
                  ))}
              </div>
            )}
          </div>
          <div
            className="timer num"
            style={{ color: countdownColor }}
            aria-live="polite"
          >
            {expired
              ? "Approval expired"
              : `Expires in ${formatCountdown(countdown)}`}
          </div>
        </div>

        {/* Body */}
        <div className="tp-modal-body">
          {(isPlacing || issue) && (
            <div
              role={issue ? "alert" : "status"}
              aria-live={issue ? "assertive" : "polite"}
              style={{
                marginBottom: 12,
                padding: "10px 12px",
                background: issue
                  ? "rgba(255, 190, 80, 0.12)"
                  : "var(--tp-ink-1)",
                border: issue
                  ? "1px solid rgba(255, 190, 80, 0.45)"
                  : "var(--tp-border-soft)",
                borderRadius: 4,
                color: issue ? "var(--tp-warn)" : "var(--tp-fg-2)",
                fontSize: 12,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  color: issue ? "var(--tp-warn)" : "var(--tp-fg-3)",
                  textTransform: "uppercase",
                  letterSpacing: ".08em",
                  marginBottom: 4,
                }}
              >
                {isPlacing && "Confirming"}
                {!isPlacing &&
                  issue?.kind === "approval_blocked" &&
                  "Approval blocked"}
                {!isPlacing &&
                  issue?.kind === "limits_changed" &&
                  "Limits changed"}
                {!isPlacing &&
                  issue?.kind === "price_drift" &&
                  "Price drift"}
                {!isPlacing &&
                  issue?.kind === "order_placement_failed" &&
                  "Order placement failed"}
              </div>
              {isPlacing
                ? "Submitting the confirmed bracket order to the broker."
                : issue?.detail}
            </div>
          )}

          {/* Order + Risk grid */}
          <div className="tp-approval-grid">
            <div>
              <div
                style={{
                  fontSize: 11,
                  color: "var(--tp-fg-3)",
                  textTransform: "uppercase",
                  letterSpacing: ".08em",
                  marginBottom: 6,
                }}
              >
                Order
              </div>
              <div className="tp-kv">
                <div className="k">Side / Qty:</div>
                <div className="v">
                  {t.side === "long" ? "Long" : "Short"} qty{" "}
                  <span className="num">{t.qty}</span>
                </div>
                <div className="k">Entry:</div>
                <div className="v num">{fmtPx(t.entry)}</div>
                <div className="k">Stop:</div>
                <div className="v num neg">{fmtPx(t.stop)}</div>
                <div className="k">Targets:</div>
                <div className="v num pos">
                  {t.targets.map((x) => fmtPx(x)).join(", ")}
                </div>
                <div className="k">Timeframe:</div>
                <div className="v">{t.timeframe}</div>
                <div className="k">Strategy:</div>
                <div
                  className="v num"
                  style={{ color: "var(--tp-fg-3)", fontSize: 12 }}
                >
                  {t.strategyAge}
                </div>
              </div>
              <div className="tp-size-editor">
                <label htmlFor="approval-risk-size">Dollar risk</label>
                <div className="tp-size-editor-row">
                  <input
                    id="approval-risk-size"
                    className="tp-input"
                    type="number"
                    min="1"
                    step="1"
                    value={riskInput}
                    disabled={!onResize || isPlacing || resizing}
                    onChange={(e) => setRiskInput(e.target.value)}
                  />
                  <button
                    type="button"
                    className="tp-btn tp-btn-secondary tp-btn-sm"
                    disabled={
                      !onResize ||
                      !riskInputValid ||
                      !sizeDirty ||
                      isPlacing ||
                      resizing
                    }
                    onClick={() => void saveSize()}
                  >
                    {resizing ? "Saving..." : "Update"}
                  </button>
                </div>
                <div className="tp-size-editor-meta">
                  {preview ? (
                    <>
                      derived qty{" "}
                      <span className="num">{preview.derivedQty}</span> ·
                      risk/unit{" "}
                      <span className="num">{fmtUSD(preview.riskPerShare)}</span>
                    </>
                  ) : (
                    "Preview unavailable until this approval is reviewed."
                  )}
                </div>
                {resizeError && (
                  <div className="tp-size-editor-error">{resizeError}</div>
                )}
              </div>
            </div>

            {/* Risk callout. Dollar risk is the loudest piece (UX §8.6.2). */}
            <div className="tp-risk-callout">
              <div className="label">If stop fills</div>
              <div className="num">{riskValueLabel}</div>
              <div className="meta">{fmtPct(t.riskPct)} of equity</div>
              {targetGain !== null && (
                <div className="tp-target-gain">
                  <span>If target fills</span>
                  <strong className="num pos">{fmtUSD(targetGain)}</strong>
                  {rr !== null && Number.isFinite(rr) && (
                    <em>{rr.toFixed(2)}R</em>
                  )}
                </div>
              )}
              {t.timeframe === "Swing" && t.gapRisk > 0 && (
                <div className="gap">
                  Overnight gap risk{" "}
                  <span className="num neg">-{fmtUSD(t.gapRisk)}</span>{" "}
                  <span style={{ color: "var(--tp-fg-3)" }}>
                    · {t.gapRiskPct.toFixed(2)}%
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* AI rationale (Standard / Careful) */}
          {mode !== "express" && (
            <div className="tp-ai-block" style={{ marginTop: 14 }}>
              <div className="head">
                {t.scoreKnown === false ? (
                  <>
                    <span>Signal review</span>
                    <span style={{ color: "var(--tp-fg-3)" }}>
                      not recorded
                    </span>
                  </>
                ) : (
                  <>
                    <span>AI rationale</span>
                    <span style={{ color: "var(--tp-fg-2)" }}>
                      conf {t.ai}
                    </span>
                    <span style={{ color: "var(--tp-fg-3)" }}>·</span>
                    <span style={{ color: "var(--tp-fg-2)" }}>
                      det {t.det}
                    </span>
                    <span
                      className={`tp-conf-${div.tier}`}
                      style={{ marginLeft: 4 }}
                    >
                      Δ{t.ai - t.det} {div.text}
                    </span>
                  </>
                )}
              </div>
              <div className="quote">"{t.reason}"</div>
              <div className="cited">{t.cited}</div>
            </div>
          )}

          {t.agentReview && (
            <div
              style={{
                marginTop: 10,
                padding: "10px 12px",
                background: "var(--tp-ink-1)",
                border: "var(--tp-border-soft)",
                borderRadius: 4,
                fontSize: 12,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  color: "var(--tp-fg-3)",
                  textTransform: "uppercase",
                  letterSpacing: ".08em",
                  marginBottom: 4,
                }}
              >
                Agent review summary
              </div>
              <div style={{ color: "var(--tp-fg-1)" }}>
                {t.agentReview.synthesisSummary}
              </div>
              {t.agentReview.keyRisks.length > 0 && (
                <div style={{ color: "var(--tp-fg-3)", marginTop: 6 }}>
                  Risk focus: {t.agentReview.keyRisks.slice(0, 2).join("; ")}
                </div>
              )}
            </div>
          )}

          {mode === "careful" && t.detRationale && (
            <div
              style={{
                marginTop: 10,
                padding: "10px 12px",
                background: "var(--tp-ink-1)",
                border: "var(--tp-border-soft)",
                borderRadius: 4,
                fontSize: 12,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  color: "var(--tp-fg-3)",
                  textTransform: "uppercase",
                  letterSpacing: ".08em",
                  marginBottom: 4,
                }}
              >
                Deterministic scorer says
              </div>
              <div className="num" style={{ color: "var(--tp-fg-1)" }}>
                {t.detRationale}
              </div>
            </div>
          )}

          {(t.lensFlag || t.aiRiskFlags.length > 0) && (
            <div
              style={{
                marginTop: 10,
                padding: "10px 12px",
                background: "var(--tp-ink-1)",
                border: "var(--tp-border-soft)",
                borderRadius: 4,
                fontSize: 12,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  color: "var(--tp-fg-3)",
                  textTransform: "uppercase",
                  letterSpacing: ".08em",
                  marginBottom: 6,
                }}
              >
                Lens review
              </div>
              {t.lensFlag && (
                <div style={{ color: "var(--tp-fg-1)" }}>{t.lensFlag}</div>
              )}
              {t.aiRiskFlags.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 6,
                    marginTop: t.lensFlag ? 8 : 0,
                  }}
                >
                  {t.aiRiskFlags.map((flag) => (
                    <span
                      key={flag}
                      style={{
                        padding: "3px 6px",
                        border: "1px solid rgba(255, 190, 80, 0.35)",
                        borderRadius: 4,
                        color: "var(--tp-warn)",
                        background: "rgba(255, 190, 80, 0.08)",
                      }}
                    >
                      {flag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Express mode: compact AI/Det one-liner */}
          {mode === "express" && (
            <div
              style={{
                marginTop: 10,
                fontSize: 12,
                color: "var(--tp-fg-3)",
                fontFamily: "var(--tp-font-mono)",
              }}
            >
              AI {t.ai} · Det {t.det}{" "}
              <span className={`tp-conf-${div.tier}`}>
                Δ{t.ai - t.det} {div.text}
              </span>{" "}
              · model: {t.aiModelId ?? "not recorded"}
            </div>
          )}

          {/* Basket awareness (UX §8.6.9) */}
          {t.basket && (
            <div className="tp-basket-callout">
              <div className="head">Basket exposure</div>
              <div className="row">
                <span className="name">{t.basket.name}</span>{" "}
                <span className="num">
                  {t.basket.exposureBefore}% → {t.basket.exposureAfter}% /{" "}
                  {t.basket.cap}% cap
                </span>{" "}
                {t.basket.warningPctOfCap !== undefined && (
                  <span style={{ color: "var(--tp-warn)", marginLeft: 8 }}>
                    ⚠ {t.basket.warningPctOfCap}% of cap after this trade
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Limit checks summary. Express omits entirely; Standard
             collapses pass-only rows; Careful renders the available
             per-check table from approval signal metadata. */}
          {mode !== "express" && (
            <div
              style={{
                marginTop: 12,
                padding: "10px 12px",
                background: "var(--tp-ink-1)",
                border: "var(--tp-border-soft)",
                borderRadius: 4,
                fontSize: 12,
                color: "var(--tp-fg-2)",
              }}
            >
              {mode === "careful" ? (
                <>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--tp-fg-3)",
                      textTransform: "uppercase",
                      letterSpacing: ".08em",
                      marginBottom: 6,
                    }}
                  >
                    Limit checks
                  </div>
                  {t.limitChecks.length > 0 ? (
                    <div
                      role="table"
                      aria-label="Limit checks"
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1.2fr 0.6fr 0.8fr 0.8fr 1.4fr",
                        gap: "6px 10px",
                        alignItems: "start",
                      }}
                    >
                      {["Check", "Status", "Value", "Limit", "Detail"].map(
                        (h) => (
                          <div
                            key={h}
                            role="columnheader"
                            style={{
                              color: "var(--tp-fg-3)",
                              fontSize: 11,
                              textTransform: "uppercase",
                              letterSpacing: ".08em",
                            }}
                          >
                            {h}
                          </div>
                        ),
                      )}
                      {t.limitChecks.map((row) => (
                        <div role="row" style={{ display: "contents" }} key={row.name}>
                          <div role="cell">{row.name}</div>
                          <div
                            role="cell"
                            style={{
                              color:
                                row.status === "fail"
                                  ? "var(--tp-bad)"
                                  : row.status === "warn"
                                    ? "var(--tp-warn)"
                                    : "var(--tp-good)",
                            }}
                          >
                            {limitStatusLabel(row.status)}
                          </div>
                          <div role="cell" className="num">
                            {row.value ?? "-"}
                          </div>
                          <div role="cell" className="num">
                            {row.limit ?? "-"}
                          </div>
                          <div role="cell" style={{ color: "var(--tp-fg-3)" }}>
                            {row.detail ?? "-"}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ color: "var(--tp-fg-3)" }}>
                      Per-check limit rows were not recorded on this approval.
                    </div>
                  )}
                </>
              ) : (
                <>
                  {t.limitChecks.length > 0
                    ? `✓ ${t.limitChecks.filter((r) => r.status === "pass").length} limit checks pass`
                    : "Limit checks passed at approval creation."}
                </>
              )}
            </div>
          )}

          {/* Acknowledgments */}
          {acks.length > 0 && (
            <div
              className="tp-ack-block"
              style={{ marginTop: 14 }}
              role="group"
              aria-label="Required acknowledgments"
            >
              <div
                style={{
                  fontSize: 11,
                  color: "var(--tp-fg-3)",
                  textTransform: "uppercase",
                  letterSpacing: ".08em",
                  marginBottom: 6,
                }}
              >
                Acknowledgments ({acks.length})
              </div>
              {acks.map((a) => (
                <div
                  key={a.key}
                  className={
                    "tp-ack" + (a.emphasis === "danger" ? " danger" : "")
                  }
                >
                  <input
                    id={`ack-${a.key}`}
                    type="checkbox"
                    checked={checked[a.key]}
                    disabled={isPlacing}
                    onChange={(e) =>
                      setChecked({ ...checked, [a.key]: e.target.checked })
                    }
                  />
                  <label htmlFor={`ack-${a.key}`}>{a.text}</label>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="tp-modal-foot">
          <span
            className="num"
            style={{ color: "var(--tp-fg-3)", fontSize: 11 }}
          >
            <span className="tp-kbd">Esc</span> cancels
            {acks.length === 0 && (
              <>
                {" "}
                · <span className="tp-kbd">⌘↵</span>{" "}
                {blocked ? "blocked by review" : "approves"}
              </>
            )}
            {acks.length > 0 && (
              <>
                {" "}
                · <span className="tp-kbd">⌘↵</span>{" "}
                {blocked
                  ? "blocked by review"
                  : ready
                    ? "approves"
                    : "(check all to enable)"}
              </>
            )}
          </span>
          <div className="actions">
            <button
              type="button"
              className={
                "tp-btn " +
                (mode === "careful" ? "tp-btn-secondary" : "tp-btn-secondary")
              }
              disabled={isPlacing}
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className={
                "tp-btn " +
                (mode === "careful"
                  ? "tp-btn-danger-solid"
                  : "tp-btn-primary")
              }
              disabled={
                !ready ||
                expired ||
                isPlacing ||
                blocked ||
                !riskInputValid ||
                sizeDirty ||
                resizing
              }
              onClick={() => void onApprove(approveAcks())}
            >
              {isPlacing
                ? "Placing..."
                : sizeDirty
                  ? "Update size first"
                : mode === "express"
                  ? "Approve"
                  : "Approve & place"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
